import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

// --- CONFIGURATION ---
const EMAIL_CONFIG = {
  SERVICE_ID: "service_8c833ki",
  TEMPLATE_ID: "template_faigbs8",
  PUBLIC_KEY: "R_XVdPg9lmKX4Mw_5"
};

const BASE_SITE_URL = "https://craigmldsouza.web.app";

// --- ASSET LOADER HOOK ---
const useExternalAssets = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    const loadFont = () => {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
    };

    Promise.all([
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js'),
      loadScript('https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js')
    ]).then(() => {
      loadFont();
      if (window.emailjs) window.emailjs.init(EMAIL_CONFIG.PUBLIC_KEY);
      setLoaded(true);
    }).catch(err => console.error("Failed to load dependencies", err));

  }, []);

  return loaded;
};

// --- HOOKS ---
const useScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const updateScroll = () => {
      const currentProgress = window.scrollY;
      const scrollHeight = document.body.scrollHeight - window.innerHeight;
      if (scrollHeight > 0) {
        setProgress(Number((currentProgress / scrollHeight).toFixed(2)) * 100);
      }
    };
    window.addEventListener('scroll', updateScroll);
    return () => window.removeEventListener('scroll', updateScroll);
  }, []);
  return progress;
};

const useScramble = (text, startTrigger = true) => {
  const [displayedText, setDisplayedText] = useState(text);
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*";

  useEffect(() => {
    if (!startTrigger) return;
    let iteration = 0;
    const interval = setInterval(() => {
      setDisplayedText(prev => 
        text.split("").map((letter, index) => {
          if (index < iteration) return text[index];
          return letters[Math.floor(Math.random() * letters.length)];
        }).join("")
      );
      if (iteration >= text.length) clearInterval(interval);
      iteration += 1 / 3;
    }, 30);
    return () => clearInterval(interval);
  }, [text, startTrigger]);

  return displayedText;
};

// --- ICONS ---
const IconWrapper = ({ size = 20, className, children, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>{children}</svg>
);
const Github = p => <IconWrapper {...p}><path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4"/><path d="M9 18c-4.51 2-5-2-7-2"/></IconWrapper>;
const Linkedin = p => <IconWrapper {...p}><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></IconWrapper>;
const Mail = p => <IconWrapper {...p}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></IconWrapper>;
const ExternalLink = p => <IconWrapper {...p}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></IconWrapper>;
const ChevronDown = p => <IconWrapper {...p}><path d="m6 9 6 6 6-6"/></IconWrapper>;
const MenuIcon = p => <IconWrapper {...p}><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></IconWrapper>;
const X = p => <IconWrapper {...p}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></IconWrapper>;
const Code = p => <IconWrapper {...p}><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></IconWrapper>;
const Battery = p => <IconWrapper {...p}><rect x="2" y="7" width="16" height="10" rx="2" ry="2"></rect><line x1="22" y1="11" x2="22" y2="13"></line></IconWrapper>;
const BatteryCharging = p => <IconWrapper {...p}><path d="M5 18H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3.19M15 6h2a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-3.19"></path><line x1="23" y1="13" x2="23" y2="11"></line><polyline points="11 6 7 12 13 12 9 18"></polyline></IconWrapper>;
const Smartphone = p => <IconWrapper {...p}><rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/></IconWrapper>;
const ShieldCheck = p => <IconWrapper {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/><path d="m9 12 2 2 4-4"/></IconWrapper>;
const Zap = p => <IconWrapper {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></IconWrapper>;
const Sun = p => <IconWrapper {...p}><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></IconWrapper>;
const Moon = p => <IconWrapper {...p}><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></IconWrapper>;
const Phone = p => <IconWrapper {...p}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></IconWrapper>;
const MapPin = p => <IconWrapper {...p}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></IconWrapper>;

// --- SUB-COMPONENTS ---

const LoadingScreen = ({ onComplete, theme }) => {
  const [progress, setProgress] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const isLight = theme === 'light';
  
  useEffect(() => {
    let currentProgress = 0;
    const timer = setInterval(() => {
      currentProgress += Math.random() * 8;
      if (currentProgress >= 100) {
        currentProgress = 100;
        clearInterval(timer);
        setTimeout(() => {
          setFadeOut(true);
          setTimeout(onComplete, 1000);
        }, 600);
      }
      setProgress(currentProgress);
    }, 100);
    return () => clearInterval(timer);
  }, []);

  const color = isLight ? '#4f46e5' : '#22d3ee';

  return (
    <div className={`fixed inset-0 z-[300] flex flex-col items-center justify-center font-mono transition-all duration-1000 ${fadeOut ? 'opacity-0 scale-110' : 'opacity-100 scale-100'} ${isLight ? 'bg-slate-50' : 'bg-[#050505]'}`}>
      
      {/* HUD Corner Decorations */}
      <div className={`absolute top-12 left-12 w-20 h-20 border-t-2 border-l-2 transition-all duration-700 ${progress > 20 ? 'opacity-40' : 'opacity-0'}`} style={{ borderColor: color }} />
      <div className={`absolute top-12 right-12 w-20 h-20 border-t-2 border-r-2 transition-all duration-700 ${progress > 40 ? 'opacity-40' : 'opacity-0'}`} style={{ borderColor: color }} />
      <div className={`absolute bottom-12 left-12 w-20 h-20 border-b-2 border-l-2 transition-all duration-700 ${progress > 60 ? 'opacity-40' : 'opacity-0'}`} style={{ borderColor: color }} />
      <div className={`absolute bottom-12 right-12 w-20 h-20 border-b-2 border-r-2 transition-all duration-700 ${progress > 80 ? 'opacity-40' : 'opacity-0'}`} style={{ borderColor: color }} />

      <div className="relative group">
        {/* Kinetic Geometric Core */}
        <svg className="w-56 h-56 md:w-72 md:h-72 animate-[spin_12s_linear_infinite]" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="48" stroke={color} strokeWidth="0.2" fill="none" strokeDasharray="5 10" />
          <path d="M50 2 A48 48 0 0 1 98 50" stroke={color} strokeWidth="2" fill="none" strokeDasharray="1 10" className="opacity-40" />
          <path d="M50 98 A48 48 0 0 1 2 50" stroke={color} strokeWidth="2" fill="none" strokeDasharray="1 10" className="opacity-40" />
        </svg>

        {/* Pulsing Scanner Radar */}
        <div className={`absolute inset-0 rounded-full animate-pulse border transition-all duration-1000 ${isLight ? 'border-indigo-100 bg-indigo-50/10' : 'border-cyan-900/20 bg-cyan-900/5'}`} />

        {/* Central Data Readout */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className={`text-5xl md:text-6xl font-bold tracking-tighter tabular-nums ${isLight ? 'text-indigo-600' : 'text-cyan-400'}`}>
            {Math.floor(progress).toString().padStart(2, '0')}
          </div>
          <div className={`text-[9px] uppercase tracking-[0.5em] mt-2 font-black ${isLight ? 'text-slate-400' : 'text-slate-600'}`}>
            System_Initialize
          </div>
        </div>
      </div>

      {/* Dynamic Status Bar */}
      <div className="mt-16 w-full max-w-xs space-y-4 px-8">
        <div className={`h-[1px] w-full relative ${isLight ? 'bg-slate-200' : 'bg-white/5'}`}>
          <div className="absolute top-0 left-0 h-full transition-all duration-300" style={{ width: `${progress}%`, backgroundColor: color, boxShadow: `0 0 10px ${color}` }} />
        </div>
        <div className="flex justify-between text-[8px] uppercase tracking-widest font-bold opacity-60">
          <span>Uplink: Primary</span>
          <span className="animate-pulse">Active</span>
        </div>
      </div>

      {/* Floating Log Overlay */}
      <div className="fixed bottom-12 left-12 hidden md:block space-y-1">
        {["neural_core", "phys_engine", "asset_cache", "ui_bridge"].map((sys, i) => (
          <div key={sys} className={`text-[8px] uppercase tracking-tighter flex gap-2 transition-opacity duration-500 ${progress > (i * 25) ? 'opacity-40' : 'opacity-0'}`}>
            <span className={isLight ? 'text-indigo-600' : 'text-cyan-400'}>[{sys}]</span>
            <span>Verified</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const NeuralNetworkScene = ({ isLowPower, theme }) => {
  const mountRef = useRef(null);
  const isLight = theme === 'light';

  useEffect(() => {
    if (!mountRef.current || !window.THREE || isLowPower) return;
    const THREE = window.THREE;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5)); 
    mountRef.current.appendChild(renderer.domElement);

    const particleCount = 100;
    const particleGeo = new THREE.BufferGeometry();
    const particlePos = new Float32Array(particleCount * 3);
    const velocities = [];
    const color = isLight ? 0x4f46e5 : 0x22d3ee;

    for(let i = 0; i < particleCount; i++) {
      particlePos[i*3] = (Math.random() - 0.5) * 15;
      particlePos[i*3+1] = (Math.random() - 0.5) * 15;
      particlePos[i*3+2] = (Math.random() - 0.5) * 15;
      velocities.push({ x: (Math.random()-0.5)*0.02, y: (Math.random()-0.5)*0.02, z: (Math.random()-0.5)*0.02 });
    }
    particleGeo.setAttribute('position', new THREE.BufferAttribute(particlePos, 3));
    const particleMat = new THREE.PointsMaterial({ color: color, size: 0.12, transparent: true, opacity: isLight ? 0.3 : 0.8 });
    const particles = new THREE.Points(particleGeo, particleMat);
    scene.add(particles);

    const lineMat = new THREE.LineBasicMaterial({ color: color, transparent: true, opacity: isLight ? 0.05 : 0.12 });
    camera.position.z = 10;
    let animationId, mouseX = 0, mouseY = 0;
    const onMouseMove = (e) => { mouseX = (e.clientX/window.innerWidth)*2-1; mouseY = -(e.clientY/window.innerHeight)*2+1; };
    window.addEventListener('mousemove', onMouseMove);

    let lines;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      particles.rotation.y += 0.0015;
      particles.rotation.x += mouseY * 0.03;
      particles.rotation.y += mouseX * 0.03;
      const positions = particles.geometry.attributes.position.array;
      for(let i = 0; i < particleCount; i++) {
        positions[i*3] += velocities[i].x; positions[i*3+1] += velocities[i].y; positions[i*3+2] += velocities[i].z;
        if(Math.abs(positions[i*3]) > 8) velocities[i].x *= -1;
        if(Math.abs(positions[i*3+1]) > 8) velocities[i].y *= -1;
        if(Math.abs(positions[i*3+2]) > 8) velocities[i].z *= -1;
      }
      particles.geometry.attributes.position.needsUpdate = true;
      const linePositions = [];
      for(let i = 0; i < particleCount; i++) {
        for(let j = i + 1; j < particleCount; j++) {
          const dist = Math.sqrt(Math.pow(positions[i*3]-positions[j*3],2) + Math.pow(positions[i*3+1]-positions[j*3+1],2) + Math.pow(positions[i*3+2]-positions[j*3+2],2));
          if (dist < 4.0) linePositions.push(positions[i*3], positions[i*3+1], positions[i*3+2], positions[j*3], positions[j*3+1], positions[j*3+2]);
        }
      }
      if (lines) { scene.remove(lines); lines.geometry.dispose(); }
      const lineGeo = new THREE.BufferGeometry();
      lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
      lines = new THREE.LineSegments(lineGeo, lineMat);
      lines.rotation.copy(particles.rotation);
      scene.add(lines);
      renderer.render(scene, camera);
    };
    animate();
    const handleResize = () => { camera.aspect = window.innerWidth / window.innerHeight; camera.updateProjectionMatrix(); renderer.setSize(window.innerWidth, window.innerHeight); };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId); scene.clear(); renderer.dispose();
      if (mountRef.current?.firstChild) mountRef.current.removeChild(mountRef.current.firstChild);
    };
  }, [isLowPower, theme, isLight]);

  return <div ref={mountRef} className="absolute inset-0 z-0 opacity-40 pointer-events-none transition-opacity duration-1000" />;
};

const CustomCursor = ({ theme }) => {
  const cursorRef = useRef(null);
  const cursorOuterRef = useRef(null);
  const isLight = theme === 'light';

  useEffect(() => {
    const moveCursor = (e) => {
      if (cursorRef.current && cursorOuterRef.current) {
        cursorRef.current.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
        setTimeout(() => {
          if (cursorOuterRef.current)
            cursorOuterRef.current.style.transform = `translate3d(${e.clientX - 16}px, ${e.clientY - 16}px, 0)`;
        }, 40);
      }
    };

    const handleHover = () => {
      if (cursorOuterRef.current) {
        cursorOuterRef.current.classList.add('scale-150');
        cursorOuterRef.current.style.borderColor = isLight ? '#4f46e5' : '#22d3ee';
        cursorOuterRef.current.style.backgroundColor = isLight ? 'rgba(79,70,229,0.1)' : 'rgba(34,211,238,0.1)';
      }
    };
    const handleLeave = () => {
      if (cursorOuterRef.current) {
        cursorOuterRef.current.classList.remove('scale-150');
        cursorOuterRef.current.style.borderColor = isLight ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.3)';
        cursorOuterRef.current.style.backgroundColor = 'transparent';
      }
    };

    window.addEventListener('mousemove', moveCursor);
    const clickables = document.querySelectorAll('a, button, input, .interactive');
    clickables.forEach(el => {
      el.addEventListener('mouseenter', handleHover);
      el.addEventListener('mouseleave', handleLeave);
    });

    return () => {
      window.removeEventListener('mousemove', moveCursor);
      clickables.forEach(el => {
        el.removeEventListener('mouseenter', handleHover);
        el.removeEventListener('mouseleave', handleLeave);
      });
    };
  }, [isLight]);

  return (
    <>
      <div ref={cursorRef} className={`hidden md:block fixed top-0 left-0 w-2.5 h-2.5 rounded-full pointer-events-none z-[400] ${isLight ? 'bg-indigo-600 shadow-[0_0_8px_rgba(79,70,229,0.4)]' : 'bg-white'}`} style={{ mixBlendMode: isLight ? 'normal' : 'difference' }} />
      <div ref={cursorOuterRef} className={`hidden md:block fixed top-0 left-0 w-10 h-10 border rounded-full pointer-events-none z-[399] transition-all duration-200 ease-out ${isLight ? 'border-slate-900/40' : 'border-white/30'}`} />
    </>
  );
};

const TerminalContact = ({ theme }) => {
  const [input, setInput] = useState('');
  const [step, setStep] = useState(0); 
  const [formData, setFormData] = useState({ message: '', name: '', email: '' });
  const [history, setHistory] = useState([
    { type: 'system', content: 'INITIALIZING SECURE_LINK...' },
    { type: 'ai', content: 'Greetings. Enter inquiry to begin.' }
  ]);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const isFirstMount = useRef(true);
  const isLight = theme === 'light';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const userMsg = { type: 'user', content: input };
    setHistory(prev => [...prev, userMsg]);
    setInput('');
    if (step === 0) {
      setFormData(prev => ({ ...prev, message: input }));
      setStep(1);
      setTimeout(() => setHistory(prev => [...prev, { type: 'ai', content: 'Message logged. Identify yourself (Enter Name):' }]), 400);
    } else if (step === 1) {
      setFormData(prev => ({ ...prev, name: input }));
      setStep(2);
      setTimeout(() => setHistory(prev => [...prev, { type: 'ai', content: `Hello ${input}. Enter email for response:` }]), 400);
    } else if (step === 2) {
      setFormData(prev => ({ ...prev, email: input }));
      setStep(3);
      setLoading(true);
      try {
        if (window.emailjs && EMAIL_CONFIG.PUBLIC_KEY !== "YOUR_PUBLIC_KEY") {
          await window.emailjs.send(EMAIL_CONFIG.SERVICE_ID, EMAIL_CONFIG.TEMPLATE_ID, {
            email: input, name: formData.name, message: formData.message, reply_to: input
          });
          setHistory(prev => [...prev, { type: 'system', content: 'UPLINK COMPLETE.' }, { type: 'ai', content: `Stand by for Craig's response.` }]);
        } else {
          setHistory(prev => [...prev, { type: 'ai', content: `Simulation active. Message stored for ${formData.name}.` }]);
        }
        setLoading(false);
      } catch (error) {
        setHistory(prev => [...prev, { type: 'system', content: 'ERROR: FAILED.' }]);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isFirstMount.current) { isFirstMount.current = false; return; }
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  return (
    <div className={`w-full max-w-3xl mx-auto border rounded-lg overflow-hidden font-mono text-sm md:text-base transition-all duration-500 ${isLight ? 'bg-white border-slate-200 shadow-xl shadow-slate-200/50' : 'bg-[#0a0a0a] border-white/10 shadow-2xl'}`}>
      <div className={`px-4 py-2 flex items-center justify-between border-b ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-[#1a1a1a] border-white/10'}`}>
        <div className="flex gap-2"><div className="w-3 h-3 rounded-full bg-red-500/50" /><div className="w-3 h-3 rounded-full bg-yellow-500/50" /><div className="w-3 h-3 rounded-full bg-green-500/50" /></div>
        <div className={`text-[10px] uppercase tracking-widest ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>guest@craig-portfolio:~/contact</div>
      </div>
      <div className={`p-6 h-64 overflow-y-auto space-y-3 custom-scrollbar transition-colors ${isLight ? 'bg-slate-50/50 text-slate-800' : 'bg-black/80 text-slate-300'}`}>
        {history.map((line, i) => (
          <div key={i} className={`${line.type === 'user' ? (isLight ? 'text-indigo-600 font-bold' : 'text-cyan-400') : line.type === 'system' ? (isLight ? 'text-amber-600 text-[10px]' : 'text-yellow-500/80 text-[10px] mt-2') : ''}`}>
            <span className="opacity-50 mr-3">{line.type === 'user' ? '➜' : line.type === 'ai' ? '🤖' : '⚡'}</span>
            {line.content}
          </div>
        ))}
        {loading && <div className={`${isLight ? 'text-indigo-600' : 'text-cyan-500'} animate-pulse`}>Uploading...</div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={handleSubmit} className={`p-4 border-t flex gap-2 items-center ${isLight ? 'bg-white border-slate-200' : 'bg-black border-white/10'}`}>
        <span className={isLight ? 'text-indigo-600' : 'text-cyan-500'}>➜</span>
        <input type="text" value={input} onChange={(e) => setInput(e.target.value)} className={`flex-1 bg-transparent outline-none font-mono ${isLight ? 'text-slate-900 placeholder-slate-300' : 'text-white placeholder-slate-700'}`} placeholder="Initialize..." disabled={loading || step === 3} />
        <button type="submit" disabled={loading || step === 3} className={`text-xs px-3 py-1 rounded transition-all uppercase font-bold ${isLight ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-white/10 text-cyan-400 hover:bg-cyan-500 hover:text-black'}`}>Execute</button>
      </form>
    </div>
  );
};

const TiltCard = ({ children, className }) => {
  const [rotate, setRotate] = useState({ x: 0, y: 0 });
  const handleMouseMove = useCallback((e) => {
    const card = e.currentTarget;
    const box = card.getBoundingClientRect();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    setRotate({ x: (y - box.height / 2) / 20, y: (box.width / 2 - x) / 20 });
  }, []);
  const handleMouseLeave = useCallback(() => setRotate({ x: 0, y: 0 }), []);
  return (
    <div 
      className={`transition-transform duration-200 ease-out transform preserve-3d interactive ${className}`}
      onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}
      style={{ transform: `perspective(1000px) rotateX(${rotate.x}deg) rotateY(${rotate.y}deg) scale3d(1, 1, 1)` }}
    >{children}</div>
  );
};

const HighlightedKeywords = ({ text, keywords, theme }) => {
  if (!keywords || keywords.length === 0) return <>{text}</>;
  const isLight = theme === 'light';
  const regex = new RegExp(`(${keywords.join('|')})`, 'gi');
  const parts = text.split(regex);
  
  return (
    <>
      {parts.map((part, i) => {
        const isKeyword = keywords.some(k => k.toLowerCase() === part.toLowerCase());
        return isKeyword ? (
          <span 
            key={i} 
            className={`font-bold transition-all duration-300 opacity-60 group-hover:opacity-100 inline-block ${
              isLight ? 'text-indigo-600' : 'text-cyan-400'
            }`}
          >
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </>
  );
};

const PreviewCard = ({ project, theme }) => {
  const isLight = theme === 'light';
  const isMobile = project.cat === 'mobile';
  const isAI = project.cat === 'ai';
  const isPatented = project.highlight && project.highlight.toLowerCase().includes('patent');

  return (
    <div className={`border-2 p-6 rounded-xl transition-all duration-500 group animate-fade-in-up flex flex-col h-full ${
      isLight ? 'bg-white border-slate-100 shadow-md hover:shadow-xl' : `bg-[#050505] shadow-2xl ${isMobile ? 'border-indigo-500/30' : (isAI ? 'border-amber-500/30' : 'border-cyan-500/30')}`
    }`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 text-left">
          <h3 className={`text-2xl font-bold flex items-center gap-3 ${isLight ? 'text-slate-900' : (isMobile ? 'text-indigo-400' : (isAI ? 'text-amber-400' : 'text-cyan-400'))}`}>
            {isMobile ? <Smartphone size={24} /> : (isPatented ? <ShieldCheck size={24} /> : <Code size={24} />)} {project.title} 
          </h3>
          <p className={`mt-2 text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>{project.desc}</p>
        </div>
        {project.url && (
          <a href={project.url} target="_blank" rel="noreferrer" className={`p-2 rounded-full transition-all ${isLight ? 'bg-slate-100 text-slate-500 hover:bg-indigo-600 hover:text-white' : 'bg-white/5 text-cyan-400 hover:bg-white/10'}`}>
            <ExternalLink size={18} />
          </a>
        )}
      </div>
      
      {project.embed && project.url && (
        <div className={`aspect-video w-full border-4 rounded-lg overflow-hidden relative mb-4 ${isLight ? 'bg-slate-200 border-slate-50' : 'bg-black/50 border-white/5'}`}>
          <iframe src={project.url} title={project.title} className="w-full h-full" frameBorder="0" loading="lazy" />
        </div>
      )}

      <div className="mt-auto pt-4 flex flex-wrap gap-2 border-t border-transparent">
        <div className={`w-full h-[1px] mb-4 ${isLight ? 'bg-slate-50' : 'bg-white/5'}`} />
        {project.tech && project.tech.map(t => ( 
          <span key={t} className={`text-[10px] font-mono border px-2 py-0.5 rounded ${isLight ? 'text-slate-400 border-slate-200 bg-slate-50' : 'text-slate-500 border-slate-800 bg-white/[0.02]'}`}>#{t}</span> 
        ))}
        {project.highlight && (
          <span className={`ml-auto text-[9px] font-mono tracking-widest uppercase py-1 px-2 rounded-full border ${isPatented ? (isLight ? 'border-amber-200 bg-amber-50 text-amber-600' : 'border-amber-500/30 bg-amber-500/10 text-amber-500') : (isLight ? 'border-slate-200 bg-slate-50 text-slate-400' : 'border-white/10 bg-white/5 text-slate-500')}`}>
            {project.highlight}
          </span>
        )}
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---

const App = () => {
  const assetsLoaded = useExternalAssets();
  const [bootSequenceComplete, setBootSequenceComplete] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lowPowerMode, setLowPowerMode] = useState(false);
  const [projectFilter, setProjectFilter] = useState('all');
  const [theme, setTheme] = useState('light');
  const isLight = theme === 'light';
  
  const scrollProgress = useScrollProgress();
  const nameText = useScramble("Craig Dsouza", bootSequenceComplete);

  const experiences = useMemo(() => [
    { 
      role: "Software Developer", 
      company: "Western Railways", 
      date: "May '25 - Jun '25", 
      desc: "Engineered agentic AI pipelines to automate reporting, reducing manual effort by 35%. Implemented drift detection for deployed LLMs.", 
      keywords: ["agentic AI pipelines", "reporting", "drift detection", "LLMs"] 
    },
    { 
      role: "Web Developer", 
      company: "Nashabandimandal", 
      date: "Jan '24 - Jul '24", 
      desc: "Re-architected full-stack systems, boosting engagement by 30%. Introduced analytics dashboards using Node.js and Supabase.", 
      keywords: ["full-stack systems", "analytics dashboards", "Node.js", "Supabase"] 
    },
    { 
      role: "Lead Software Developer", 
      company: "Aakriti Exports", 
      date: "Sep '23 - Jan '24", 
      desc: "Delivered the company's first digital platform, increasing conversions by 20%. Established CI/CD workflows with Docker.", 
      keywords: ["digital platform", "conversions", "CI/CD", "Docker"] 
    }
  ], []);

  const projects = useMemo(() => [
    { title: "Onyx AR", cat: "mobile", desc: "Jewelry customization app for iOS featuring real-time AR try-on.", tech: ["Swift", "ARKit", "Combine"], highlight: "iOS Exclusive", url: `${BASE_SITE_URL}/onyx`, embed: false },
    { title: "Daura", cat: "systems", desc: "Bespoke digital platform for luxury jewelry presentation.", tech: ["React", "Motion"], highlight: "Retail", url: `${BASE_SITE_URL}/daura`, embed: false },
    { title: "Indra", cat: "systems", desc: "Performance storefront for heavy-duty industrial manufacturing.", tech: ["Next.js", "Vercel"], highlight: "Live", url: `${BASE_SITE_URL}/indra`, embed: true },
    { title: "Lumiere", cat: "systems", desc: "Designed to showcase luxury jewelry with speed, clarity, and visual precision.", tech: ["Next.js", "React", "Tailwind"], highlight: "Retail", url: `${BASE_SITE_URL}/lumiere`, embed: true },
    { title: "Synthetica AI", cat: "ai", desc: "Patented text classifier achieving 99.67% accuracy using DistilBERT.", tech: ["Python", "Flask", "DistilBERT"], highlight: "Patented", url: "https://synthetica-3bf2d.web.app/", embed: true },
    { title: "Ashes of Aurum", cat: "games", desc: "Browser strategy game with progressive difficulty and particle visuals.", tech: ["JavaScript", "Canvas"], highlight: "Strategy", url: `${BASE_SITE_URL}/aoa`, embed: true },
    { title: "Stack 2026", cat: "games", desc: "Precision stacking challenge testing timing and spatial awareness.", tech: ["Vanilla JS", "Physics"], highlight: "Arcade", url: `${BASE_SITE_URL}/stak2026`, embed: true },
    { title: "CatCode", cat: "games", desc: "Classic side-scroller demonstrating foundational game logic.", tech: ["HTML5", "Logic"], highlight: "Legacy", url: `${BASE_SITE_URL}/catcode`, embed: true },
    { title: "Bob the Blob", cat: "games", desc: "Physics-based platformer focusing on fluid motion dynamics.", tech: ["p5.js", "Matter.js"], highlight: "Experimental", url: `${BASE_SITE_URL}/bob`, embed: true },
    { title: "LittleRock", cat: "systems", desc: "Core service architecture for a modern development agency.", tech: ["AWS", "Full Stack"], highlight: "Business", url: `${BASE_SITE_URL}/littlerock`, embed: true },
    { title: "Aakriti Exports", cat: "systems", desc: "B2B platform for global electrical machinery export logistics.", tech: ["Node.js", "Docker"], highlight: "Industrial", url: `${BASE_SITE_URL}/aakriti-exports`, embed: false },
    { title: "JaldiBol", cat: "mobile", desc: "AI background service auto-generating Meet links via WhatsApp triggers.", tech: ["Kotlin", "Accessibility API", "Gemini"], highlight: "AI Powered", url: `${BASE_SITE_URL}/jaldibol`, embed: false },
    { title: "PocketNAS", cat: "mobile", desc: "Local network storage ecosystem combining multiple mobile devices.", tech: ["Dart", "WebSockets", "Networking"], highlight: "Cross-Platform", url: `${BASE_SITE_URL}/pocketnas`, embed: false },
    { title: "NeuroNav", cat: "ai", desc: "Real-time pothole detection utilizing sensor fusion and telemetry.", tech: ["OpenCV", "TensorFlow", "IoT"], highlight: "National Winner", url: null, embed: false },
    { title: "GIL Refiner", cat: "ai", desc: "LLM prompt engineering tool designed to reduce hallucinations.", tech: ["GenAI", "FastAPI", "spaCy"], highlight: "Research", url: "https://github.com/CraigMLdsouza/GIL", embed: false },
    { title: "FAIA", cat: "ai", desc: "Functionally Autonomous and Intelligent Agents", tech: ["Llama 3", "Pinecone", "RAG"], highlight: "Beta", url: "https://github.com/CraigMLdsouza/FAIA", embed: false },
    { title: "Plumber", cat: "systems", desc: "Modular framework for building and managing AI processing pipelines.", tech: ["Python", "FastAPI", "LLM APIs"], highlight: "Internal Tooling", embed: false },
    { title: "SFIT Assets", cat: "systems", desc: "ERP tracking inventory for 1500+ institutional users.", tech: ["React", "SQL"], highlight: "Enterprise", url: "https://sfitassets.web.app/", embed: false }
  ], []);

  const filteredProjects = useMemo(() => projectFilter === 'all' ? projects : projects.filter(p => p.cat === projectFilter), [projectFilter, projects]);

  const scrollTo = useCallback((id) => {
    setIsMenuOpen(false);
    const el = document.getElementById(id);
    if(el) {
        const offset = 80;
        const bodyRect = document.body.getBoundingClientRect().top;
        window.scrollTo({ top: el.getBoundingClientRect().top - bodyRect - offset, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const sections = ['home', 'about', 'skills', 'experience', 'projects', 'contact'];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el && el.getBoundingClientRect().top >= -200 && el.getBoundingClientRect().top <= window.innerHeight * 0.4) {
          setActiveSection(section); break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      body { margin: 0; background: ${isLight ? '#f8fafc' : '#050505'}; overflow-x: hidden; font-family: 'Space Grotesk', sans-serif; cursor: none; transition: background 0.6s ease-in-out; }
      @media (pointer: coarse) { body { cursor: auto; } }
      * { scrollbar-width: thin; scrollbar-color: ${isLight ? '#4f46e5 #f1f5f9' : '#22d3ee #050505'}; transition: scrollbar-color 0.6s ease; }
      ::-webkit-scrollbar { width: 8px; }
      ::-webkit-scrollbar-track { background: ${isLight ? '#f1f5f9' : '#050505'}; }
      ::-webkit-scrollbar-thumb { background: ${isLight ? '#cbd5e1' : '#1a1a1a'}; border: 2px solid ${isLight ? '#f1f5f9' : '#050505'}; border-radius: 10px; transition: background 0.6s ease; }
      ::-webkit-scrollbar-thumb:hover { background: ${isLight ? '#4f46e5' : '#22d3ee'}; }
      .preserve-3d { transform-style: preserve-3d; }
      .text-glow { text-shadow: 0 0 20px rgba(34, 211, 238, 0.5); }
      @keyframes fade-in-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      .animate-fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
      .glitch-wrapper { position: relative; display: inline-block; }
      .glitch-wrapper::before, .glitch-wrapper::after { content: attr(data-text); position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: ${isLight ? '#f8fafc' : '#050505'}; transition: background 0.6s ease; }
      .glitch-wrapper::before { left: 2px; text-shadow: -1px 0 ${isLight ? '#4f46e5' : '#ff00c1'}; clip: rect(44px, 450px, 56px, 0); animation: glitch-anim 5s infinite linear alternate-reverse; }
      .glitch-wrapper::after { left: -2px; text-shadow: -1px 0 ${isLight ? '#0ea5e9' : '#00fff9'}; clip: rect(44px, 450px, 56px, 0); animation: glitch-anim2 5s infinite linear alternate-reverse; }
      @keyframes glitch-anim { 0% { clip: rect(33px, 9999px, 11px, 0); } 100% { clip: rect(9px, 9999px, 98px, 0); } }
      @keyframes glitch-anim2 { 0% { clip: rect(2px, 9999px, 44px, 0); } 100% { clip: rect(50px, 9999px, 90px, 0); } }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [isLight]);

  if (!assetsLoaded || !bootSequenceComplete) return <LoadingScreen onComplete={() => setBootSequenceComplete(true)} theme={theme} />;

  return (
    <div className={`min-h-screen transition-colors duration-700 ${isLight ? 'bg-slate-50 text-slate-800' : 'bg-[#050505] text-slate-300'}`}>
      <CustomCursor theme={theme} />
      <div className={`fixed top-0 left-0 h-1 z-[400] transition-all duration-700 ${isLight ? 'bg-indigo-600 shadow-[0_0_10px_#4f46e5]' : 'bg-cyan-500 shadow-[0_0_10px_#22d3ee]'}`} style={{ width: `${scrollProgress}%` }} />
      
      <div className="fixed inset-0 pointer-events-none">
        <NeuralNetworkScene isLowPower={lowPowerMode} theme={theme} />
        <div className={`absolute inset-0 z-10 transition-colors duration-700 ${isLight ? 'bg-slate-50/20' : 'bg-black/40'}`} />
      </div>

      <nav className={`fixed w-full z-50 transition-all duration-500 border-b ${scrolled ? (isLight ? 'bg-white/90 backdrop-blur-md border-slate-100 py-4 shadow-sm' : 'bg-[#050505]/90 backdrop-blur-md border-white/5 py-4') : 'bg-transparent border-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className={`text-xl font-bold tracking-tighter flex items-center gap-2 cursor-pointer group interactive ${isLight ? 'text-slate-900' : 'text-white'}`} onClick={() => scrollTo('home')}>
            <span className={isLight ? 'text-indigo-600 font-mono' : 'text-cyan-400 font-mono'}>{`{`}</span>CD<span className={isLight ? 'text-indigo-600 font-mono' : 'text-cyan-400 font-mono'}>{`}`}</span>
          </div>
          <div className="hidden md:flex gap-10">
            {['About', 'Skills', 'Experience', 'Projects'].map(item => (
              <button key={item} onClick={() => scrollTo(item.toLowerCase())} className={`text-sm font-medium tracking-wide transition-all relative group interactive ${activeSection === item.toLowerCase() ? (isLight ? 'text-indigo-600' : 'text-cyan-400') : (isLight ? 'text-slate-500' : 'text-slate-400')}`}>
                {item}
                <span className={`absolute -bottom-1 left-0 h-[1px] transition-all ${isLight ? 'bg-indigo-600' : 'bg-cyan-400'} ${activeSection === item.toLowerCase() ? 'w-full' : 'w-0 group-hover:w-full'}`} />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')} className={`p-2 rounded-full border transition-all interactive ${isLight ? 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200' : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'}`}>
              {isLight ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <button onClick={() => scrollTo('contact')} className={`hidden md:block px-5 py-2 text-xs font-bold uppercase tracking-widest rounded-sm interactive transition-all ${isLight ? 'bg-indigo-600 text-white shadow-lg' : 'bg-cyan-400 text-black'}`}>Contact</button>
            <button className={`md:hidden interactive ${isLight ? 'text-slate-900' : 'text-white'}`} onClick={() => setIsMenuOpen(!isMenuOpen)}>{isMenuOpen ? <X /> : <MenuIcon />}</button>
          </div>
        </div>
      </nav>

      <section id="home" className="relative min-h-screen flex items-center justify-center pt-20 px-6 z-20 transition-colors duration-700">
        <div className="max-w-7xl w-full grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 text-left">
            <div className={`inline-flex items-center gap-3 px-4 py-2 border rounded-full backdrop-blur-md animate-fade-in-up ${isLight ? 'bg-indigo-50 border-indigo-100' : 'bg-white/5 border-white/10'}`}>
              <span className={`relative flex h-2 w-2 rounded-full ${isLight ? 'bg-indigo-600' : 'bg-cyan-500'}`} />
              <span className={`text-xs font-mono tracking-wider uppercase ${isLight ? 'text-indigo-600' : 'text-cyan-400'}`}>Online Interface</span>
            </div>
            <div>
              <h1 className={`text-6xl md:text-8xl font-bold tracking-tighter leading-none mb-4 glitch-wrapper ${isLight ? 'text-slate-900' : 'text-white'}`} data-text={nameText}>{nameText}</h1>
              <p className={`text-xl md:text-2xl font-light tracking-wide max-w-lg ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
                AI/ML Engineer & Full-Stack Architect.<br />
                <span className={`text-lg font-mono mt-2 block ${isLight ? 'text-indigo-500' : 'text-cyan-400/80'}`}>Synthesizing intelligent ecosystems.</span>
              </p>
            </div>
            <div className="flex gap-4">
              <button onClick={() => scrollTo('projects')} className={`px-8 py-4 font-bold text-sm uppercase tracking-widest rounded-sm shadow-xl transition-all ${isLight ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-white text-black hover:bg-cyan-400'}`}>Explore Labs</button>
              <div className="flex gap-4 items-center pl-4">
                {[{ Icon: Github, href: "https://github.com/CraigMLdsouza" }, { Icon: Linkedin, href: "https://linkedin.com/in/craig-ml-dsouza" }].map(({ Icon, href }, i) => (
                  <a key={i} href={href} target="_blank" rel="noreferrer" className={`transition-all hover:scale-110 interactive ${isLight ? 'text-slate-400 hover:text-indigo-600' : 'text-slate-400 hover:text-cyan-400'}`}><Icon size={24} /></a>
                ))}
              </div>
            </div>
          </div>
          <div className="hidden lg:flex justify-end relative">
            <TiltCard className={`relative w-80 h-96 border backdrop-blur-md rounded-xl p-8 flex flex-col justify-between transition-all duration-500 ${isLight ? 'bg-white border-slate-100 shadow-2xl shadow-slate-200/50' : 'bg-gradient-to-br from-white/10 to-white/5 border-white/10 shadow-2xl'}`}>
              <div>
                <div className={`text-xs font-mono mb-2 ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>SYSTEM_STATUS</div>
                <div className={`text-2xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`}>Ready for Ops</div>
              </div>
              <div className="space-y-4">
                {[{ label: "ML Models", val: "Patented & Prod" }, { label: "Tech Stack", val: "Cross-Vertical" }, { label: "Geo", val: "Mumbai, IN" }].map((stat) => (
                  <div key={stat.label} className={`flex justify-between items-center border-b pb-2 ${isLight ? 'border-slate-100' : 'border-white/5'}`}>
                    <span className={`text-[10px] uppercase font-mono ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>{stat.label}</span>
                    <span className={`text-sm font-mono ${isLight ? 'text-indigo-600' : 'text-cyan-400'}`}>{stat.val}</span>
                  </div>
                ))}
              </div>
              <div className="pt-4 flex justify-between items-end">
                <div className={`w-12 h-12 rounded border flex items-center justify-center transition-all ${isLight ? 'bg-indigo-50 border-indigo-100 text-indigo-600 shadow-indigo-100/50 shadow-lg' : 'border-cyan-500/30 text-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.2)]'}`}><Zap size={24} /></div>
                <span className={`text-6xl font-bold absolute bottom-4 right-4 ${isLight ? 'text-slate-100' : 'text-white/5'}`}>05</span>
              </div>
            </TiltCard>
          </div>
        </div>
      </section>

      <section id="about" className={`py-32 relative z-20 transition-colors duration-700 ${isLight ? 'bg-white' : 'bg-[#050505]'}`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4 text-left">
              <h2 className={`text-4xl font-bold mb-6 ${isLight ? 'text-slate-900' : 'text-white'}`}>About<span className={isLight ? 'text-indigo-600' : 'text-cyan-400'}>.</span></h2>
              <div className={`h-1 w-20 mb-8 ${isLight ? 'bg-indigo-600' : 'bg-cyan-500'}`} />
              <p className={`text-lg leading-relaxed ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>Building high-accuracy <span className={isLight ? 'text-indigo-600 font-bold' : 'text-cyan-400'}>AI pipelines</span> and intelligent <span className={isLight ? 'text-indigo-600 font-bold' : 'text-cyan-400'}>mobile apps</span> with focused architectural precision.</p>
            </div>
            <div className="md:col-span-8 grid sm:grid-cols-2 gap-6">
              {[
                { sub: "B.E. Computer Eng", title: "Education", org: "SFIT Mumbai" },
                { sub: "National Winner", title: "Achievement", org: "Smart India Hackathon" },
                { sub: "Agentic AI", title: "Specialization", org: "Advanced LLMs" },
                { sub: "Full Stack", title: "Engineering", org: "Cross-platform" }
              ].map(card => (
                <div key={card.sub} className={`border p-6 rounded-lg transition-all text-left ${isLight ? 'bg-slate-50 border-slate-100 hover:shadow-lg' : 'bg-white/[0.02] border-white/5 hover:bg-white/[0.05]'}`}>
                  <h3 className={`text-xl font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`}>{card.sub}</h3>
                  <p className={`text-xs uppercase tracking-widest ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>{card.title}</p>
                  <p className={`text-[10px] font-mono mt-1 ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>{card.org}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="skills" className={`py-32 relative z-20 transition-colors duration-700 ${isLight ? 'bg-slate-50 border-y border-slate-100' : 'bg-[#080808] border-y border-white/5'}`}>
        <div className="max-w-7xl mx-auto px-6 text-left">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              { cat: "AI & Intelligence", s: ["Agentic AI", "RAG Systems", "LLM Fine-tuning", "PyTorch", "TensorFlow", "DistilBERT", "NLP Pipelines", "LangChain", "Vector Databases", "Prompt Engineering"] },
              { cat: "Mobility & Core", s: ["Flutter / Dart", "Swift / ARKit", "Accessibility API", "Native Android", "Local NAS Ecosystem", "WebRTC", "BLE Integration", "Shared Networking"] },
              { cat: "System Engine", s: ["Node.js", "FastAPI", "AWS Bedrock", "Docker / K8s", "CI/CD Workflows", "Microservices", "Supabase", "PostgreSQL", "MongoDB", "Auth0"] },
              { cat: "Interface Design", s: ["React / Next.js", "Three.js", "Tailwind CSS", "Framer Motion", "Canvas API", "Responsive UI", "TypeScript", "GSAP Animations", "UI/UX Architecture"] }
            ].map((group) => (
              <div key={group.cat} className="space-y-6">
                <div className="flex items-center gap-3">
                  <span className={`w-1 h-8 rounded-full ${isLight ? 'bg-indigo-600' : 'bg-cyan-500'}`} />
                  <h3 className={`font-bold text-xl uppercase tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`}>{group.cat}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {group.s.map(skill => (
                    <span key={skill} className={`px-4 py-2 text-sm border transition-all duration-300 rounded-full hover:scale-105 interactive ${
                      isLight 
                      ? 'bg-white border-slate-200 text-slate-600 hover:border-indigo-600 hover:text-indigo-600 hover:shadow-lg' 
                      : 'bg-white/5 border-white/5 text-slate-400 hover:border-cyan-500 hover:text-cyan-400'
                    }`}>
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="experience" className={`py-32 relative z-20 transition-colors duration-700 ${isLight ? 'bg-white' : 'bg-[#050505]'}`}>
        <div className="max-w-5xl mx-auto px-6 border-l space-y-16 border-slate-200 text-left">
            <div className="mb-12">
              <h2 className={`text-4xl font-bold mb-4 ${isLight ? 'text-slate-900' : 'text-white'}`}>Professional Trajectory</h2>
              <div className={`h-1 w-24 ${isLight ? 'bg-indigo-600' : 'bg-cyan-500'}`} />
            </div>
            {experiences.map(exp => (
              <div key={exp.company} className="relative pl-12 group interactive">
                <div className={`absolute left-[-5px] top-2 w-[11px] h-[11px] border rounded-full bg-white transition-all duration-500 group-hover:scale-150 ${
                  isLight ? 'border-indigo-600 group-hover:shadow-[0_0_15px_#4f46e5]' : 'border-cyan-400 bg-black group-hover:shadow-[0_0_15px_#22d3ee]'
                }`} />
                <div className="flex flex-col md:flex-row justify-between md:items-center gap-2 mb-4">
                   <h3 className={`text-3xl font-bold transition-colors ${isLight ? 'text-slate-900 group-hover:text-indigo-600' : 'text-white group-hover:text-cyan-400'}`}>{exp.role}</h3>
                   <span className={`font-mono text-xs px-3 py-1 rounded border transition-colors ${isLight ? 'text-slate-400 bg-slate-50 border-slate-100 group-hover:text-indigo-500' : 'text-slate-500 bg-white/5 border-white/5 group-hover:text-cyan-400'}`}>{exp.date}</span>
                </div>
                <div className={`text-xl font-medium mb-6 transition-opacity group-hover:opacity-100 opacity-60 ${isLight ? 'text-slate-800' : 'text-slate-300'}`}>
                  {exp.company}
                </div>
                <p className={`text-lg leading-relaxed transition-colors ${isLight ? 'text-slate-500 group-hover:text-slate-700' : 'text-slate-400 group-hover:text-slate-200'}`}>
                  <HighlightedKeywords text={exp.desc} keywords={exp.keywords} theme={theme} />
                </p>
              </div>
            ))}
        </div>
      </section>

      <section id="projects" className={`py-32 border-t relative z-20 overflow-hidden transition-colors duration-500 ${isLight ? 'bg-slate-50 border-slate-100' : 'bg-[#080808] border-white/5'}`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-16 gap-8 text-left">
            <div>
              <h2 className={`text-4xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`}>Labs & Deployments</h2>
              <p className={`text-sm mt-2 font-mono uppercase tracking-widest ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>Operational Registry v.2026</p>
            </div>
            <div className={`flex flex-wrap justify-center gap-2 p-1 rounded-full border backdrop-blur-sm ${isLight ? 'bg-slate-900/5 border-slate-900/5' : 'bg-black/40 border-white/5'}`}>
              {['all', 'ai', 'mobile', 'games', 'systems'].map(cat => (
                <button key={cat} onClick={() => setProjectFilter(cat)} className={`px-4 py-1 text-[10px] uppercase font-bold rounded-full transition-all interactive ${projectFilter === cat ? (isLight ? 'bg-indigo-600 text-white shadow-lg' : 'bg-cyan-500 text-black shadow-lg shadow-cyan-500/20') : (isLight ? 'text-slate-400 bg-transparent hover:bg-indigo-50' : 'text-slate-500 bg-transparent hover:bg-white/5 hover:text-white')}`}>{cat}</button>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8 min-h-[400px]">
            {filteredProjects.map(p => <PreviewCard key={p.title} project={p} theme={theme} />)}
          </div>
        </div>
      </section>

      <section id="contact" className={`py-32 relative z-20 overflow-hidden transition-colors duration-500 ${isLight ? 'bg-white bg-gradient-to-t from-indigo-50/50 to-transparent' : 'bg-gradient-to-t from-cyan-950/10 to-[#050505]'}`}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className={`text-5xl font-bold mb-4 ${isLight ? 'text-slate-900' : 'text-white'}`}>
              Secure <span className={isLight ? 'text-indigo-600' : 'text-cyan-400'}>Uplink</span>
            </h2>
            <p className={`font-mono text-xs uppercase tracking-widest ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>Initialize Connection Protocol</p>
          </div>
          <TerminalContact theme={theme} />
          
          <footer className={`mt-32 pt-16 border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-left">
              <div className="space-y-6">
                <div className={`text-2xl font-bold tracking-tighter ${isLight ? 'text-slate-900' : 'text-white'}`}>
                  Craig Dsouza<span className={isLight ? 'text-indigo-600' : 'text-cyan-400'}>.</span>
                </div>
                <div className="flex items-center gap-3 opacity-60">
                   <MapPin size={16} className={isLight ? 'text-indigo-600' : 'text-cyan-400'} />
                   <span className="text-sm font-medium">Mumbai, India</span>
                </div>
                <p className="text-xs leading-relaxed opacity-50 uppercase tracking-widest">
                  Architecting intelligent systems through focused engineering and creative logic.
                </p>
              </div>

              <div className="space-y-6">
                <div className={`text-xs font-mono uppercase tracking-[0.3em] ${isLight ? 'text-indigo-600' : 'text-cyan-400'}`}>Contact_Inquiry</div>
                <div className="space-y-4">
                  <a href="tel:+917249478960" className="flex items-center gap-3 group interactive">
                    <div className={`p-2 rounded-lg transition-colors ${isLight ? 'bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white' : 'bg-white/5 group-hover:bg-cyan-500 group-hover:text-black'}`}>
                      <Phone size={14} />
                    </div>
                    <span className="text-sm font-medium transition-colors group-hover:opacity-100 opacity-70">+91 7249478960</span>
                  </a>
                  <a href="mailto:dsouzacraigmichael@gmail.com" className="flex items-center gap-3 group interactive">
                    <div className={`p-2 rounded-lg transition-colors ${isLight ? 'bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white' : 'bg-white/5 group-hover:bg-cyan-500 group-hover:text-black'}`}>
                      <Mail size={14} />
                    </div>
                    <span className="text-sm font-medium transition-colors group-hover:opacity-100 opacity-70">dsouzacraigmichael@gmail.com</span>
                  </a>
                </div>
              </div>

              <div className="space-y-6">
                <div className={`text-xs font-mono uppercase tracking-[0.3em] ${isLight ? 'text-indigo-600' : 'text-cyan-400'}`}>Network_Links</div>
                <div className="flex flex-wrap gap-3">
                  {[
                    { label: "Github", href: "https://github.com/CraigMLdsouza", icon: Github },
                    { label: "Linkedin", href: "https://linkedin.com/in/craig-ml-dsouza", icon: Linkedin }
                  ].map((link) => (
                    <a key={link.label} href={link.href} target="_blank" rel="noreferrer" 
                       className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all interactive ${
                         isLight ? 'border-slate-200 hover:border-indigo-600 hover:text-indigo-600' : 'border-white/10 hover:border-cyan-400 hover:text-cyan-400'
                       }`}>
                      <link.icon size={14} />
                      <span className="text-xs font-bold uppercase tracking-tighter">{link.label}</span>
                    </a>
                  ))}
                </div>
                <button 
                  onClick={() => setLowPowerMode(!lowPowerMode)} 
                  className={`flex items-center gap-2 text-[10px] font-mono tracking-widest transition-colors ${isLight ? 'hover:text-indigo-600 text-slate-300' : 'hover:text-cyan-400 text-slate-700'}`}
                >
                  {lowPowerMode ? <BatteryCharging size={12} className={isLight ? 'text-indigo-600' : 'text-cyan-400'} /> : <Battery size={12} />}
                  {lowPowerMode ? "ECO_MODE: ACTIVE" : "ECO_MODE: DISABLED"}
                </button>
              </div>
            </div>
            <div className="mt-16 pt-8 border-t border-transparent flex justify-center opacity-30 text-[9px] uppercase tracking-[0.5em]">
              © 2026 CRAIG M. L. DSOUZA • ALL RIGHTS RESERVED
            </div>
          </footer>
        </div>
      </section>
    </div>
  );
};

export default App;